import { Container, IconButton, Tooltip} from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { BooleanField, Confirm, Datagrid, FunctionField, List, TextField, useListContext, useNotify, useRedirect, useTranslate } from 'react-admin'
import { CardCommon } from 'src/components/uiElements'
import { ListViewActionsButtons } from 'src/components/uiElements/buttons/ListViewActionsButtons'
import { ListViewTopToolbar } from 'src/components/uiElements/CRUD/ListViewTopToolbar'
import { hasPermissionFunction } from 'src/helper/HasPermission'
import { PostFilterFormSupplierPaymentOrder, SupplierPaymentOrderFilters } from './logic/SupplierPaymentOrderFilters'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { HasPermission } from "src/helper/HasPermission"
import { supplierPaymentOrderServices } from 'src/services/purchases/supplierPaymentOrder/SupplierPaymentOrder.service'

const CustomPaymentButton = ({ record, id }) => {
  const translate = useTranslate()
  const redirect = useRedirect()

  const paymentId = record?.id ?? id
  if (!paymentId) return null

  const handleClick = () =>{
    redirect(`./payment?id=${paymentId}`)
  }

  return (
    <HasPermission resource='supplier-payment-order-detail' permission='create'>
      <Tooltip
        title={translate('ra.action.pay')}
        placement='top'
      >
        <IconButton size='small' aria-label='view reservation' onClick={handleClick}>
          <AttachMoneyIcon fontSize='small' color='primary'/>
        </IconButton>
      </Tooltip>
    </HasPermission>
  )
};


const CustomAuthorizeButton = ({ record, id }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const { refetch } = useListContext()
  const [loading, setLoading] = useState(false)
  const [openConfirm, setOpenConfirm] = useState(false)

  const paymentId = record?.id ?? id
  const isAuthorized = record?.is_authorized

  const handleClick = () => {
    setOpenConfirm(true)
  }

  const handleConfirm = useCallback(async () => {
    try {
      setLoading(true)
      if (isAuthorized) {
        await supplierPaymentOrderServices.unauthorize(paymentId)
        notify('ra.notification.unauthorized_success', { type: 'success' })
      } else {
        await supplierPaymentOrderServices.authorize(paymentId)
        notify('ra.notification.authorized_success', { type: 'success' })
      }
      refetch()
    } catch (error) {
      notify('ra.notification.error', { type: 'error' })
    } finally {
      setLoading(false)
      setOpenConfirm(false)
    }
  }, [paymentId, isAuthorized])

  if (!paymentId) return null

  const Icon = isAuthorized ? LockOpenIcon : VerifiedUserIcon
  const tooltipKey = isAuthorized ? 'ra.action.unauthorize' : 'ra.action.authorize'
  const confirmTitle = isAuthorized ? 'ra.action.unauthorize' : 'ra.action.authorize'
  const confirmContent = isAuthorized ? 'ra.message.confirm_unauthorize' : 'ra.message.confirm_authorize'

  return (
    <HasPermission resource='supplier-payment-order-detail' permission='revision'>
      <Tooltip
        title={translate(tooltipKey)}
        placement='top'
      >
        <span>
          <IconButton
            size='small'
            aria-label='authorize payment order'
            onClick={handleClick}
            disabled={loading}
          >
            <Icon fontSize='small' color={isAuthorized ? 'error' : 'primary'} />
          </IconButton>
        </span>
      </Tooltip>
      <Confirm
        isOpen={openConfirm}
        loading={loading}
        title={confirmTitle}
        content={confirmContent}
        onConfirm={handleConfirm}
        onClose={() => setOpenConfirm(false)}
      />
    </HasPermission>
  )
};


export const SupplierPaymentOrderList = (props) => {
    const translate = useTranslate()
    const { isFetching, page, filterValues } = useListContext()
    const prevPageRef = useRef()
  
    const prevFiltersRef = useRef(filterValues)
  
    const [pageChanged, setPageChanged] = useState(false)

    const allFilters = [
      { source: 'supplier_id', name: 'ra.field.supplier' },
      { source: 'created_at', name: 'ra.field.created_at' },
      { source: 'status', name: 'ra.field.status' },
      { source: 'due_date', name: 'ra.field.due_date' },
    ]
  
    useEffect(() => {
      // Comprobar si la página ha cambiado y si isFetching es true
      if (prevPageRef.current !== page && isFetching) {
        prevPageRef.current = page
        setPageChanged(true)
      }
      if (prevFiltersRef.current !== filterValues && isFetching) {
        prevFiltersRef.current = filterValues
        setPageChanged(true)
      } else {
        setPageChanged(false)
      }
    }, [isFetching, page, filterValues])

    const hasPermissions = () => {
        return(
            hasPermissionFunction('supplier-payment-order', 'edit') ||
            hasPermissionFunction('supplier-payment-order', 'delete')
        )
    }
  return (
    <Container className='content'>
      <CardCommon title={translate('ra.entity.supplier-payment-order')}>
        <List {...props} sort={{field: 'id', order: 'DESC'}} component='div' actions={<ListViewTopToolbar customFilter FilterFormComponent={PostFilterFormSupplierPaymentOrder} FilterButtonComponent={SupplierPaymentOrderFilters} listFilters={allFilters} exporterDisable={false}/>}>
          <Datagrid  /* setSort={onClickSort} */ isLoading={pageChanged}>
            <TextField source='id' label={translate('ra.field.id')} />
            <TextField source='supplier_name' label={translate('ra.field.supplier')} />
            <TextField source='billing_code' label={translate('ra.field.code')} />
            <TextField source='amount' label={translate('ra.field.amount')} />
            <BooleanField label={translate('ra.field.status')} source='status' sortable={false} />
            {/* <TextField source='observations' label={translate('ra.field.observations')} /> */}
            <TextField source='due_date' label={translate('ra.field.due_date')} />
            {/* {hasPermissions() && (
              <ListViewActionsButtons label={translate('ra.action.actions')} fieldsName={['short_name']} genderElement='femenine' disableRevision />
            )} */}
            <FunctionField
              label={translate('ra.action.actions')}
              render={record =>
                <div style={{display:'flex', flexDirection:'row', alignItems:'center'}}>
                  <ListViewActionsButtons
                    CustomButton={!record.status ? CustomPaymentButton : null}
                    disableDelete={record.status || record.is_authorized}
                    disableEdit={record.status || record.is_authorized}
                    label={translate('ra.action.actions')}
                    fieldsName={['short_name']}
                    genderElement='femenine'
                    disableRevision
                  />
                  {record?.id && !record.status && <CustomAuthorizeButton id={record.id} record={record} />}
                </div>
              }
          />
          </Datagrid>
        </List>
      </CardCommon>
    </Container>
  )
}
